import axios from 'axios';
import { store } from '@/app/store';
import { setShouldCallAgent } from '@/features/Global/slice';

type ConnectionListener = (connected: boolean) => void;
type MessageListener = (message: any) => void;

interface KnockKnockResponse {
	status: string;
	timestamp: string;
	agentId: string;
	version: string;
}

interface HandshakeResponse {
	token: string;
	expires: string;
	agentId: string;
}

const AGENT_BASE_URL = import.meta.env.VITE_ATA_AGENT_BASE_URL;
const WS_BASE_URL = import.meta.env.VITE_ATA_AGENT_WS_URL;
const POLL_INTERVAL = 20000;

/**
 * Singleton manager for desktop agent connectivity.
 * - Performs knockknock + handshake
 * - Keeps a WebSocket open as source-of-truth connectivity
 * - Notifies subscribers on status changes
 * - Updates Redux only for UI consumption (not as source-of-truth)
 */
class AgentConnectionManager {
	private static instance: AgentConnectionManager | null = null;

	private ws: WebSocket | null = null;
	private pollInterval: number | null = null;
	private isConnecting = false;
	private listeners = new Set<ConnectionListener>();
	private messageListeners = new Set<MessageListener>();
	private started = false;

	static getInstance(): AgentConnectionManager {
		if (!AgentConnectionManager.instance) {
			AgentConnectionManager.instance = new AgentConnectionManager();
		}
		return AgentConnectionManager.instance;
	}

	start(): void {
		if (this.started) return;
		this.started = true;
		this.startPolling();
	}

	stop(): void {
		this.started = false;
		this.stopPolling();
		this.closeWebSocket();
		this.setConnected(false);
	}

	isConnected(): boolean {
		return this.ws?.readyState === WebSocket.OPEN;
	}

	isConnectingNow(): boolean {
		return this.isConnecting;
	}

	subscribe(listener: ConnectionListener): () => void {
		this.listeners.add(listener);
		// Emit current status immediately
		try {
			listener(this.isConnected());
		} catch {}
		return () => this.listeners.delete(listener);
	}

	subscribeToMessages(listener: MessageListener): () => void {
		this.messageListeners.add(listener);
		return () => this.messageListeners.delete(listener);
	}

	private notifyMessage(message: any): void {
		this.messageListeners.forEach((l) => {
			try {
				l(message);
			} catch (error) {
				console.error('Error in message listener:', error);
			}
		});
	}

	private notify(): void {
		const connected = this.isConnected();
		this.listeners.forEach((l) => {
			try {
				l(connected);
			} catch {}
		});
	}

	private setConnected(connected: boolean): void {
		store.dispatch(setShouldCallAgent({ shouldCallAgent: connected }));
		this.notify();
	}

	private stopPolling(): void {
		if (this.pollInterval) {
			window.clearInterval(this.pollInterval);
			this.pollInterval = null;
		}
	}

	private startPolling(): void {
		// If already connected or connecting, no-op
		if (this.pollInterval || this.isConnected()) return;
		const knock = async () => {
			if (this.isConnected() || this.isConnecting) return;
			await this.knockKnock();
		};
		// Fire immediately, then schedule
		knock();
		this.pollInterval = window.setInterval(knock, POLL_INTERVAL);
	}

	private closeWebSocket(): void {
		if (this.ws) {
			try {
				this.ws.close();
			} catch {}
			this.ws = null;
		}
	}

	private async knockKnock(): Promise<void> {
		try {
			const response = await axios.get<KnockKnockResponse>(
				`${AGENT_BASE_URL}/agent/knockknock`
			);
			if (response.data?.status === 'ok') {
				await this.performHandshake();
			} else {
				this.setConnected(false);
			}
		} catch (error) {
			console.error('KnockKnock failed:', error);
			this.setConnected(false);
		}
	}

	private async performHandshake(): Promise<void> {
		try {
			const response = await axios.get<HandshakeResponse>(
				`${AGENT_BASE_URL}/agent/handshake`,
				{
					headers: {
						accessToken: `${localStorage.getItem('accessToken') as string}`,
						refreshToken: `${localStorage.getItem('refreshToken') as string}`,
					},
				}
			);
			const { token } = response.data;
			if (token) {
				this.connectWebSocket(token);
			} else {
				console.error('No token received from handshake');
				this.setConnected(false);
			}
		} catch (error) {
			console.error('Handshake failed:', error);
			this.setConnected(false);
		}
	}

	private connectWebSocket(token: string): void {
		if (this.isConnecting || this.ws?.readyState === WebSocket.OPEN) return;

		this.isConnecting = true;
		this.closeWebSocket();

		try {
			const ws = new WebSocket(`${WS_BASE_URL}/agent/ws?token=${token}`);
			this.ws = ws;

			ws.onopen = () => {
				this.isConnecting = false;
				this.stopPolling();
				this.setConnected(true);
			};

			ws.onclose = () => {
				this.isConnecting = false;
				this.ws = null;
				this.setConnected(false);
				if (this.started) this.startPolling();
			};

			ws.onerror = (error) => {
				console.error('WebSocket error:', error);
				this.isConnecting = false;
				this.ws = null;
				this.setConnected(false);
				if (this.started) this.startPolling();
			};

			ws.onmessage = (event) => {
				try {
					const message = JSON.parse(event.data);
					this.notifyMessage(message);
				} catch (error) {
					console.error('Error parsing WebSocket message:', error);
				}
			};
		} catch (error) {
			console.error('Failed to create WebSocket connection:', error);
			this.isConnecting = false;
			this.setConnected(false);
		}
	}
}

export const agentConnectionManager = AgentConnectionManager.getInstance();
